<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col gap-3">
      <h3 class="font-semibold text-lg">{{ title }}</h3>
      <p class="text-gray-600 text-sm">
        {{ description }}
      </p>
      
      <p v-if="modelValue.length === 0" class="text-gray-500 text-sm italic">
        Nenhuma conta na lista. Adicione pelo menos uma conta abaixo.
      </p>

      <div
        v-for="row in modelValue"
        :key="row.accountId"
        class="flex flex-col gap-3 p-4 border border-surface-200 rounded-lg bg-surface-50"
      >
        <div class="flex justify-between items-start">
          <div class="font-medium text-surface-900">{{ row.accountName }}</div>
          <Button
            v-if="allowRemoveMemberships"
            icon="material-icons:delete"
            severity="danger"
            text
            size="small"
            class="p-0"
            :disabled="disabled"
            @click="removeMembership(row.accountId)"
            title="Remover da lista"
          >
            <template #icon>
              <span class="material-icons text-red-500">delete</span>
            </template>
          </Button>
        </div>

        <div class="flex flex-wrap gap-4 items-center">
          <div class="flex items-center">
            <Checkbox
              :inputId="'adm-' + row.accountId"
              v-model="row.is_admin"
              :binary="true"
              :disabled="disabled"
            />
            <label :for="'adm-' + row.accountId" class="ml-2 cursor-pointer text-sm">Administrador da conta</label>
          </div>
        </div>

        <div class="flex flex-col max-w-md">
          <label class="text-xs font-semibold text-gray-500 mb-1" :for="'role-' + row.accountId">Papel na Conta</label>
          <Select
            :id="'role-' + row.accountId"
            v-model="row.role_id"
            :options="rolesByAccountId[row.accountId] || []"
            optionLabel="name"
            optionValue="id"
            placeholder="Selecione um papel"
            showClear
            filter
            filterPlaceholder="Buscar papel..."
            class="w-full"
            :disabled="disabled"
          />
        </div>
      </div>
    </div>

    <div v-if="showAddAccount" class="flex flex-col gap-3 border-t border-surface-200 pt-4">
      <h3 class="font-semibold text-lg">Incluir outra conta</h3>
      <div class="flex flex-col max-w-lg">
        <label for="add_membership_account" class="text-xs font-semibold text-gray-500 mb-1">Conta</label>
        <Select
          id="add_membership_account"
          :modelValue="null"
          :options="availableAccounts"
          optionLabel="name"
          optionValue="id"
          placeholder="Selecione uma conta para adicionar"
          filter
          filterPlaceholder="Buscar conta..."
          class="w-full"
          :disabled="disabled || availableAccounts.length === 0"
          @update:modelValue="onAddAccount"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Checkbox from 'primevue/checkbox';
import Select from 'primevue/select';
import Button from 'primevue/button';
import { computed } from 'vue';

export type MembershipRow = {
  accountId: string;
  accountName: string;
  is_admin: boolean;
  role_id: string | null;
};

const props = defineProps<{
  modelValue: MembershipRow[];
  availableAccounts: any[];
  rolesByAccountId: Record<string, any[]>;
  disabled?: boolean;
  allowRemoveMemberships?: boolean;
  showAddAccount?: boolean;
  title?: string;
  description?: string;
}>();

const emit = defineEmits(['update:modelValue', 'add-account']);

const title = props.title || 'Contas do usuário';
const description = props.description || 'Gerencie os vínculos do usuário com as contas do sistema.';
const allowRemoveMemberships = computed(() => props.allowRemoveMemberships ?? true);
const showAddAccount = computed(() => props.showAddAccount ?? true);

const removeMembership = (accountId: string) => {
  const newValue = props.modelValue.filter((m) => m.accountId !== accountId);
  emit('update:modelValue', newValue);
};

const onAddAccount = (accountId: string | null) => {
  if (accountId) {
    emit('add-account', accountId);
  }
};
</script>
